import { Trans, useLingui } from '@lingui/react/macro'
import { useScatterplotContext } from 'features/timeSeries'
import {
  overlayForegroundMix,
  overlayPanelSurface,
} from 'features/timeSeries/components/chartOverlays/overlayChrome'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import React from 'react'
import styled from 'styled-components'

const Notice = styled.div`
  ${overlayPanelSurface}
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
`

const ShowAllButton = styled.button`
  background: ${overlayForegroundMix(8)};
  border: none;
  border-radius: 3px;
  padding: 3px 7px;
  color: var(--foreground);
  font-family: inherit;
  font-size: 11px;
  cursor: pointer;

  &:hover {
    background: ${overlayForegroundMix(16)};
  }
`

/**
 * Says out loud that the chart is showing fewer pens than it was asked for.
 *
 * The cap is a real tradeoff — a chart plotting every sensor at a site is both
 * unreadable and pathologically expensive — but hiding the cap would recreate
 * the failure this page is built around: a chart quietly showing less than
 * reality with no way to tell. So the count is exact and the override is one
 * click, not a support ticket.
 */
export const PenCapNotice: React.FC = () => {
  const { t } = useLingui()

  const { penCap } = useScatterplotContext()

  if (!penCap.isCapped) return null

  return (
    <Notice role='status' data-testid='pen-cap-notice'>
      <span>
        <Trans>Showing </Trans>
        {penCap.shown.toLocaleString()} of {penCap.requested.toLocaleString()}{' '}
        pens
      </span>
      <ChartTooltip
        label={t`Load all ${{ value65_29: penCap.requested.toLocaleString() }} pens. This chart may take minutes to load and will be hard to read.`}
        side='top'>
        <ShowAllButton type='button' onClick={penCap.showAll}>
          <Trans>Show all</Trans>
        </ShowAllButton>
      </ChartTooltip>
    </Notice>
  )
}
